import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import logger from "@insite/client-framework/Logger";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import validatePassword from "@insite/client-framework/Store/CommonHandlers/ValidatePassword";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    getCountriesDataView,
    getCurrentCountries,
} from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadCurrentCountries, {
    loadCurrentCountriesParameter,
} from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import { getCreateAccountReturnUrl } from "@insite/client-framework/Store/Pages/CreateAccount/CreateAccountSelectors";
import translate from "@insite/client-framework/Translate";
import { CountryModel, StateModel } from "@insite/client-framework/Types/ApiModels";
import ReCaptcha from "@insite/content-library/Components/ReCaptcha";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox from "@insite/mobius/Checkbox";
import CheckboxGroup from "@insite/mobius/CheckboxGroup";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { FormFieldStyle } from "@insite/mobius/FormField";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconPresentationProps } from "@insite/mobius/Icon";
import AlertTriangle from "@insite/mobius/Icons/AlertTriangle";
import Eye from "@insite/mobius/Icons/Eye";
import EyeOff from "@insite/mobius/Icons/EyeOff";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Radio, { RadioComponentProps, RadioStyle } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupComponentProps } from "@insite/mobius/RadioGroup";
import Select, { SelectPresentationProps, SelectProps } from "@insite/mobius/Select";
import TextField, { InputStyle, TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import FieldSetPresentationProps, { FieldSetGroupPresentationProps } from "@insite/mobius/utilities/fieldSetProps";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { formatPhoneNumber } from "@sunSource/Common/Utilities/formatPhoneNumber";
import {
    CallbackType,
    ErrorObjectType,
    InputObjectType,
    validate,
    validateConfirmPasswordHandler,
    validatePasswordHandler,
} from "@sunSource/Common/Utilities/validate";
import {
    FormErrorsStateType,
    intialErrorFormState,
    newCustomerValidatorConfig,
    ValidatorConfigType,
} from "@sunSource/Components/CreateAccountNewFormValidatorConfig";
import setOnePageCreateAccountData from "@sunSource/Handlers/NewOnePageCheckout/OnePageCheckoutCreateAccountHandler";
import addAccount from "@sunSource/Store/Accounts/Handlers/AddAccount";
import CustomApplicationState from "@sunSource/Store/CustomApplicationState";
import SetOnePageTabData from "@sunSource/Store/Data/NewOnePageCheckout/Handlers/OnePageCheckoutTabHandler";
// import signIn from "@sunSource/Store/Data/NewOnePageCheckout/Handlers/SignIn";
import signIn from "@insite/client-framework/Store/Context/Handlers/SignIn";
import {
    getOnePageCheckoutAccountState,
    getOnePageCheckoutTabState,
} from "@sunSource/Store/Data/NewOnePageCheckout/OnePageCheckoutStateSelectors";
import React, { ReactNode, RefObject, useContext, useEffect, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

interface MessageFilterStyles {
    radioGroup?: FieldSetGroupPresentationProps<RadioGroupComponentProps>;
    radioButton?: FieldSetPresentationProps<RadioComponentProps>;
}

const mapStateToProps = (state: CustomApplicationState) => {
    const countriesDataView = getCountriesDataView(state, loadCurrentCountriesParameter);
    return {
        accountSettings: getSettingsCollection(state).accountSettings,
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        tabState: getOnePageCheckoutTabState(state),
        countries: getCurrentCountries(state),
        shouldLoadCountries: !countriesDataView.value && !countriesDataView.isLoading,
        createAccountData: getOnePageCheckoutAccountState(state),
        isAuthenticated: state?.context?.session?.isAuthenticated,
        onePageCheckoutPageUrl: getPageLinkByPageType(state, "OnePageCheckoutPage")?.url,
    };
};

const mapDispatchToProps = {
    signIn,
    SetOnePageTabData,
    loadCurrentCountries,
    setOnePageCreateAccountData,
    addAccount,
    validateReCaptcha: makeHandlerChainAwaitable<{ location: string; recaptchaRef: RefObject<ReCAPTCHA> }, boolean>(
        validateReCaptcha,
    ),
};

export interface CreateAccountNewFormStyles {
    form?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    centeringWrapper?: InjectableCss;
    mainGridContainer?: GridContainerProps;
    fieldsGridItem?: GridItemProps;
    emailGridItem?: GridItemProps;
    emailTextField?: TextFieldPresentationProps;
    firstNameGridItem?: GridItemProps;
    jobTitleGridItem?: GridItemProps;
    companyNameGridItem?: GridItemProps;
    addressLineOneGridItem?: GridItemProps;
    addressLineTwoGridItem?: GridItemProps;
    cityGridItem?: GridItemProps;
    zipCodeGridItem?: GridItemProps;
    lastNameGridItem?: GridItemProps;
    userNameTextField?: TextFieldPresentationProps;
    firstNameTextField?: TextFieldPresentationProps;
    jobTitleTextField?: TextFieldPresentationProps;
    lastNameTextField?: TextFieldPresentationProps;
    companyNameTextField?: TextFieldPresentationProps;
    cityTextField?: TextFieldPresentationProps;
    zipCodeTextField?: TextFieldPresentationProps;
    addressLineOneTextField?: TextFieldPresentationProps;
    addressLineTwoTextField?: TextFieldPresentationProps;
    passwordGridItem?: GridItemProps;
    passwordTextField?: TextFieldPresentationProps;
    passwordIcon?: IconPresentationProps;
    confirmPasswordGridItem?: GridItemProps;
    confirmPasswordTextField?: TextFieldPresentationProps;
    confirmPasswordIcon?: IconPresentationProps;
    stateGridItem?: GridItemProps;
    businessTypeGridItem?: GridItemProps;
    stateTextField?: SelectPresentationProps;
    countryGridItem?: GridItemProps;
    countryTextField?: SelectPresentationProps;
    phoneNumberGridItem?: GridItemProps;
    faxGridItem?: GridItemProps;
    phoneNumberTextField?: TextFieldPresentationProps;
    faxTextField?: TextFieldPresentationProps;
    subscriptionGridItem?: GridItemProps;
    submitErrorGridItem?: GridItemProps;
    submitErrorTitle?: TypographyProps;
    submitGridItem?: GridItemProps;
    reCaptchaGridItem?: GridItemProps;
    submitButton?: ButtonPresentationProps;
    requirementsGridItem?: GridItemProps;
    requirementsTitle?: TypographyProps;
    companyTypeText?: TypographyProps;
    passwordRequirementsGridItem?: GridItemProps;
    passwordRequirementsGridContainer?: GridContainerProps;
    passwordGridContainer?: GridContainerProps;
    fieldSetParentDiv?: InjectableCss;
    messageFilter?: MessageFilterStyles;
    requiredFieldDiv?: TypographyProps;
    shipToBillToCheck?: InjectableCss;
    passwordValidationMessage?: InjectableCss;
    loginBtn?: ButtonPresentationProps;
    lostYourPasswordSection?: GridItemProps;
    loginButtonSection?: GridItemProps;
    lostYourPassword?: ClickablePresentationProps;
    inputHeader?: InjectableCss;
    businessDone?: SelectPresentationProps;
    customerNumberAvailableTextField?: SelectPresentationProps;
    customerNumberTextField?: TextFieldPresentationProps;
    needHelpCustomerService?: TypographyProps;
    createPasswordTitleWrapper?: GridItemProps;
    companyDetailsGridItem?: GridItemProps;
    userDetailsWrapper?: InjectableCss;
    userLabel?: InjectableCss;
    userName?: InjectableCss;
}

export const createAccountNewFormStyles: CreateAccountNewFormStyles = {
    requiredFieldDiv: {
        css: css`
            > ${TypographyStyle} {
                margin-top: 15px;
            }
        `,
    },
    centeringWrapper: {
        css: css`
            display: flex;
            height: 300px;
            justify-content: center;
            align-items: center;
        `,
    },
    mainGridContainer: {
        css: css`
            padding: 0px 15px;
            margin-top: 20px;
            [class^="GridItemStyle"] {
                padding-bottom: 5px;
                margin-bottom: 0;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            padding: 0px 12px;
                        `,
                        null,
                        null,
                        null,
                        null,
                    ])}
            }
            [class^="GridOffset"] {
                margin-bottom: 0;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            padding: 0px 12px;
                        `,
                        null,
                        null,
                        null,
                        null,
                    ])}
            }
            .containerTitle {
                width: 100%;
                display: flex;
                margin: 0px 15px;
                > ${TypographyStyle} {
                    &:first-child {
                        font-weight: bold;
                        font-size: 18px;
                    }
                }
                [class^="CheckboxGroupStyle"] {
                    margin-left: 30px;
                }
            }
            [class^="FormInputWrapper"] {
                margin-top: 0 !important;
                > ${InputStyle} {
                    :focus {
                        border: 2px solid ${getColor("primary")} !important;
                    }
                }
                > select[class^="SelectStyle"] {
                    :focus {
                        border: 2px solid ${getColor("primary")} !important;
                    }
                }
            }
        `,
    },
    businessTypeGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
            > div[class^="RadioGroupStyle"] {
                justify-content: flex-start;
                display: inline-block;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                width: 100%;
                            `,
                            null,
                            null,
                        ],
                        "max",
                    )}
                > div[class^="RadioStyle"] {
                    margin-top: 0;
                    margin-right: 25px;
                    display: inline-block;
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            css`
                                width: 50%;
                                margin-right: 0;
                            `,
                            css`
                                width: 50%;
                                margin-right: 0;
                            `,
                            null,
                            null,
                            null,
                        ])}
                    &:last-child {
                        margin-right: 0;
                    }
                    > label[class^="TypographyStyle"] {
                        font-size: 13px;
                    }
                }
            }
        `,
    },
    fieldsGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            margin-bottom: 15px !important;
        `,
    },
    countryGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            display: none;
            > ${FormFieldStyle} {
                flex-direction: column;
                > ${TypographyStyle} {
                    text-align: left !important;
                    width: 100% !important;
                    padding: 0;
                    + div[class^="FormInputWrapper"] {
                        margin-top: 10px;
                        width: 100%;
                        > select {
                            height: 39.98px;
                        }
                    }
                }
            }
        `,
    },
    stateGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            > ${FormFieldStyle} {
                flex-direction: column;
                > ${TypographyStyle} {
                    text-align: left !important;
                    width: 100% !important;
                    padding: 0;
                    + div[class^="FormInputWrapper"] {
                        margin-top: 10px;
                        width: 100%;
                        > select {
                            height: 39.98px;
                        }
                    }
                }
            }
        `,
    },
    subscriptionGridItem: {
        width: 12,
        css: css`
            flex-wrap: wrap;
            padding: 0px 0px 0px 15px;
        `,
    },
    submitErrorGridItem: {
        width: [12, 12, 12, 6, 6],
    },
    submitErrorTitle: {
        color: "danger",
    },
    reCaptchaGridItem: { width: 12 },
    submitGridItem: {
        width: 12,
        css: css`
            justify-content: end;
            padding: 0px 0px 35px 0px;
        `,
    },
    emailTextField: {
        cssOverrides: {
            inputSelect: css`
                font-size: 16px;
                padding: 0 2rem 0 10px !important;
                &:focus {
                    padding: 0 2rem 0 10px !important;
                }
            `,
        },
    },
    fieldSetParentDiv: {
        css: css`
            display: inline-block;
            vertical-align: top;
            > ${RadioStyle} {
                white-space: nowrap;
            }
        `,
    },
    shipToBillToCheck: {
        css: css`
            margin-top: 15px;
            margin-left: 15px;
            width: 100%;
        `,
    },
    companyTypeText: {
        size: 13,
        weight: "bold",
    },
    passwordValidationMessage: {
        css: css`
            ul {
                list-style: initial;
                padding-left: 20px;
                font-size: 14px;
                font-family: OpenSans;
            }
        `,
    },

    loginBtn: {
        variant: "tertiary",
        css: css`
            width: 140px;
            text-transform: uppercase;
            > ${TypographyStyle} {
                font-weight: 400;
            }
        `,
    },
    lostYourPasswordSection: {
        width: [12, 12, 6, 6, 6],
        css: css``,
    },
    lostYourPassword: {
        css: css`
            color: ${getColor("primary")};
        `,
    },
    loginButtonSection: {
        width: [12, 12, 12, 12, 12],
        css: css`
            justify-content: right;
        `,
    },
    inputHeader: {
        css: css`
            margin-bottom: 15px;
            margin-top: 10px;
            display: block;
            font-size: 18px;
        `,
    },
    needHelpCustomerService: {
        css: css`
            margin-left: 14px;
        `,
    },
    businessDone: {
        css: css`
            margin-bottom: 15px;
        `,
    },
    createPasswordTitleWrapper: {
        css: css`
            padding-bottom: 0px !important;
            margin-top: 10px !important;
            span {
                margin: 0px;
            }
        `,
    },
    companyDetailsGridItem: {
        width: 6,
        css: css`
            paddingtop: 0px;
        `,
    },
    userDetailsWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    userLabel: {
        css: css`
            font-weight: 600;
        `,
    },
    userName: {
        css: css`
            color: gray;
        `,
    },
};

const styles = createAccountNewFormStyles;

const StyledForm = getStyledWrapper("form");
const CustomRadio = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 8px;

    input[type="radio"] {
        display: none; /* Hide default radio button */
    }

    label {
        position: relative;
        padding-left: 30px;
        cursor: pointer;
        font-size: 14px;
        color: #333;
        font-weight: 600;
    }

    label::before {
        content: "";
        position: absolute;
        left: 0;
        top: 0px;
        width: 20px;
        height: 20px;
        border-radius: 50%;
        border: 1px solid #d3d3d3;
        background-color: white;
        transition: all 0.3s;
    }

    input[type="radio"]:checked + label::before {
        border-color: #d3d3d3;
        background: white;
    }

    label::after {
        content: "";
        position: absolute;
        left: 4px;
        top: 4px;
        width: 12px;
        height: 12px;
        border-radius: 50%;
        background-color: #00539b;
        opacity: 0;
        transition: opacity 0.3s;
    }

    input[type="radio"]:checked + label::after {
        opacity: 1;
    }
`;

const OnePageCustomerValidationAndSetPasswordForm = ({
    accountSettings,
    validateReCaptcha,
    signInUrl,
    signIn,
    tabState,
    SetOnePageTabData,
    countries,
    shouldLoadCountries,
    setOnePageCreateAccountData,
    createAccountData,
    addAccount,
    isAuthenticated,
    onePageCheckoutPageUrl
}: Props) => {
    const firstNameRequiredFieldMessage = siteMessage("First name is Required");
    const lastNameRequiredFieldMessage = siteMessage("Last name is Required");
    const companyNameRequiredFieldMessage = siteMessage("Company name is Required");
    const cityRequiredFieldMessage = siteMessage("City is Required");
    const billToCityRequiredFieldMessage = siteMessage("City is Required");
    const zipCodeRequiredFieldMessage = siteMessage("Zip Code is Required");
    const billToZipCodeRequiredFieldMessage = siteMessage("Zip Code is Required");
    const addressLineOneRequiredFieldMessage = siteMessage("Address Line One is Required");
    const billToAddressLineOneRequiredFieldMessage = siteMessage("Address Line One is Required");
    const addressLineTwoRequiredFieldMessage = siteMessage("Address Line Two is Required");
    const billToAddressLineTwoRequiredFieldMessage = siteMessage("Address Line Two is Required");
    const phoneNumberRequiredFieldMessage = siteMessage("Phone number is Required");
    const faxRequiredFieldMessage = siteMessage("Fax is Required");
    const emailRequiredFieldMessage = siteMessage("CreateNewAccountInfo_EmailAddress_Required");
    const countryRequiredFieldMessage = siteMessage("Country is Required");
    const billToCountryRequiredFieldMessage = siteMessage("Country is Required");
    const stateRequiredFieldMessage = siteMessage("State is Required");
    const billToStateRequiredFieldMessage = siteMessage("State is Required");
    const jobTitleRequiredFieldMessage = siteMessage("Job Title is Required");

    const [formErrors, setFormErrors] = React.useState<FormErrorsStateType>(intialErrorFormState);
    const [firstName, setFirstName] = useState("");
    const [jobTitle, setJobTitle] = useState("");
    const [lastName, setLastName] = useState("");
    const [companyName, setCompanyName] = useState("");
    const [city, setCity] = useState("");
    const [billToCity, setBillToCity] = useState("");
    const [zipCode, setZipCode] = useState("");
    const [billToZipCode, setBillToZipCode] = useState("");
    const [addressLineOne, setAddressLineOne] = useState("");
    const [billToAddressLineOne, setBillToAddressLineOne] = useState("");
    const [addressLineTwo, setAddressLineTwo] = useState("");
    const [billToAddressLineTwo, setBillToAddressLineTwo] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [fax, setFax] = useState("");
    const [email, setEmail] = useState("");
    const [selectedState, setSelectedState] = useState<StateModel | undefined>(undefined);
    const [billToSelectedState, setBillToSelectedState] = useState<StateModel | undefined>(undefined);
    const [emailError, setEmailError] = useState(emailRequiredFieldMessage);
    const [errorMessage, setErrorMessage] = useState("");
    // const [isSubscribed, setIsSubscribed] = useState(true);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isFormSubmitted, setFormSubmitted] = React.useState(false);
    const toasterContext = useContext(ToasterContext);
    const recaptchaRef: RefObject<ReCAPTCHA> = React.createRef();
    const [isButtonDisabled, setIsButtonDisabled] = React.useState(false);
    const [password, setPassword] = React.useState("");
    const [confirmPassword, setConfirmPassword] = React.useState("");
    const [showPassword, setShowPassword] = React.useState(false);
    const [confirmPasswordErrorMessage, setConfirmPasswordErrorMessage] = React.useState<React.ReactNode>("");
    const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
    const [passwordErrorMessage, setPasswordErrorMessage] = React.useState<React.ReactNode>("");
    const [businessType, setBusinessType] = React.useState("OEM (Original Equipment Manufacturer)");
    const [IsShipToSameAsBilling, setIsShipToSameAsBillTo] = React.useState(true);

    const selCountry = countries?.find(option => option.name === "United States");
    const [firstNameError, setFirstNameError] = useState(
        accountSettings?.useEmailAsUserName ? "" : firstNameRequiredFieldMessage,
    );

    const [billToSelectedCountry, setBillToSelectedCountry] = useState<CountryModel | undefined>(selCountry);
    const [jobTitleError, setJobTitleError] = useState(
        accountSettings?.useEmailAsUserName ? "" : jobTitleRequiredFieldMessage,
    );

    const [lastNameError, setLastNameError] = useState(
        accountSettings?.useEmailAsUserName ? "" : lastNameRequiredFieldMessage,
    );

    const [companyNameError, setCompanyNameError] = useState(
        accountSettings?.useEmailAsUserName ? "" : companyNameRequiredFieldMessage,
    );

    const [cityError, setCityError] = useState(accountSettings?.useEmailAsUserName ? "" : cityRequiredFieldMessage);

    const [billToCityError, setBillToCityError] = useState(
        accountSettings?.useEmailAsUserName ? "" : billToCityRequiredFieldMessage,
    );

    const [zipCodeError, setZipCodeError] = useState(
        accountSettings?.useEmailAsUserName ? "" : zipCodeRequiredFieldMessage,
    );

    const [billToZipCodeError, setBillToZipCodeError] = useState(
        accountSettings?.useEmailAsUserName ? "" : billToZipCodeRequiredFieldMessage,
    );

    const [addressLineOneError, setAddressLineOneError] = useState(
        accountSettings?.useEmailAsUserName ? "" : addressLineOneRequiredFieldMessage,
    );

    const [billToAddressLineOneError, setBillToAddressLineOneError] = useState(
        accountSettings?.useEmailAsUserName ? "" : billToAddressLineOneRequiredFieldMessage,
    );

    const [addressLineTwoError, setAddressLineTwoError] = useState(
        accountSettings?.useEmailAsUserName ? "" : addressLineTwoRequiredFieldMessage,
    );

    const [billToAddressLineTwoError, setBillToAddressLineTwoError] = useState(
        accountSettings?.useEmailAsUserName ? "" : billToAddressLineTwoRequiredFieldMessage,
    );

    const [phoneNumberError, setPhoneNumberError] = useState(
        accountSettings?.useEmailAsUserName ? "" : phoneNumberRequiredFieldMessage,
    );

    const [faxError, setFaxError] = useState(accountSettings?.useEmailAsUserName ? "" : faxRequiredFieldMessage);

    const [countryError, setCountryError] = useState(
        accountSettings?.useEmailAsUserName ? "" : countryRequiredFieldMessage,
    );

    const [billToCountryError, setBillToCountryError] = useState(
        accountSettings?.useEmailAsUserName ? "" : billToCountryRequiredFieldMessage,
    );

    const [stateError, setStateError] = useState(accountSettings?.useEmailAsUserName ? "" : stateRequiredFieldMessage);

    const [billToStateError, setBillToStateError] = useState(
        accountSettings?.useEmailAsUserName ? "" : billToStateRequiredFieldMessage,
    );

    const [selectedCountry, setSelectedCountry] = useState<CountryModel | undefined>(selCountry);
    React.useEffect(() => {
        setIsButtonDisabled(validateSubmitDisabled(formErrors));
    }, [formErrors]);

    /*  React.useEffect(() => {
        if (selCountry) {
            setSelectedCountry(selCountry);
        }
        setCountryError("");
    }, [countries]);

    React.useEffect(() => {
        if (selCountry) {
            setBillToSelectedCountry(selCountry);
        }
        setBillToCountryError("");
    }, [countries]); */

    /*   React.useEffect(() => {
        if (shouldLoadCountries) {
            loadCurrentCountries();
        }
    }, []); */

    React.useEffect(() => {
        setShippingAddressSameAsBillingAddress(false);
        const cloneFormErrors = { ...formErrors };
        for (const [key] of Object.entries(cloneFormErrors)) {
            const element: HTMLSelectElement | HTMLInputElement | null = document.querySelector(`[name="${key}"]`);
            if (element && element.value) {
                delete cloneFormErrors[key as keyof FormErrorsStateType];
                setFormErrors(removeKey(cloneFormErrors, key));
            }
        }
    }, []);

    const resetForm = () => {
        setFirstName("");
        setFirstNameError("");
        setJobTitle("");
        setJobTitleError("");
        setLastName("");
        setLastNameError("");
        setCompanyName("");
        setCompanyNameError("");
        setCity("");
        setCityError("");
        setBillToCity("");
        setBillToCityError("");
        setZipCode("");
        setZipCodeError("");
        setBillToZipCode("");
        setBillToZipCodeError("");
        setAddressLineOne("");
        setAddressLineOneError("");
        setBillToAddressLineOne("");
        setBillToAddressLineOneError("");
        setAddressLineTwo("");
        setAddressLineTwoError("");
        setBillToAddressLineTwo("");
        setBillToAddressLineTwoError("");
        setPhoneNumber("");
        setPhoneNumberError("");
        setFax("");
        setFaxError("");
        setEmail("");
        setEmailError("");
        setCountryError("");
        setBillToCountryError("");
        setSelectedState(undefined);
        setStateError("");
        setBillToSelectedState(undefined);
        setBillToStateError("");
        setPassword("");
        setConfirmPassword("");
        setPasswordErrorMessage("");
        setConfirmPasswordErrorMessage("");
    };

    const validateSubmitDisabled = (errorsState: FormErrorsStateType) => {
        return Object.keys(errorsState).length !== 0;
    };

    const removeKey = (formErrorObject: FormErrorsStateType, deleteKey: string) => {
        return Object.assign(
            {},
            ...Object.entries(formErrorObject)
                .filter(([k]) => k !== deleteKey)
                .map(([k, v]) => ({ [k]: v })),
        );
    };

    const validateHandler = (input: HTMLInputElement | HTMLSelectElement, customValidation?: CallbackType): void => {
        const inputObject: InputObjectType = {
            name: input.name,
            value: input.value,
        };

        const validatorConfig = newCustomerValidatorConfig[input.name as keyof ValidatorConfigType];
        const isValidate: ErrorObjectType | undefined = validate(inputObject, validatorConfig, customValidation);

        if (isValidate === undefined) {
            delete formErrors[input.name as keyof FormErrorsStateType];
            setFormErrors(formErrors);
        } else {
            setFormErrors({
                ...formErrors,
                [input.name]: isValidate ? isValidate.errorMsg : isValidate,
            });
        }
        if (!validatorConfig.required && input.value === "") {
            delete formErrors[input.name as keyof FormErrorsStateType];
            setFormErrors(formErrors);
        }

        setIsButtonDisabled(validateSubmitDisabled(formErrors));
    };

    const validateConfirmPassword = (newPassword: string, newConfirmPassword: string) => {
        const errorMessage =
            newPassword !== newConfirmPassword
                ? siteMessage("CreateNewAccountInfo_PasswordCombination_DoesNotMatch")
                : "";
        setConfirmPasswordErrorMessage(errorMessage);
        return !errorMessage;
    };

    const validateSubmitEnabled = () => {
        return !(
            emailError ||
            firstNameError ||
            jobTitleError ||
            lastNameError ||
            companyNameError ||
            cityError ||
            billToCityError ||
            zipCodeError ||
            billToZipCodeError ||
            addressLineOneError ||
            billToAddressLineOneError ||
            addressLineTwoError ||
            billToAddressLineTwoError ||
            phoneNumberError ||
            faxError ||
            countryError ||
            billToCountryError ||
            stateError ||
            billToStateError ||
            passwordErrorMessage ||
            confirmPasswordErrorMessage
        );
    };

    const formatZipCode = (zipCode: string) => {
        const newZipCode = zipCode.replace(/[^\d]/g, "");
        return newZipCode;
    };

    const setShippingAddressSameAsBillingAddress = (isChecked: boolean) => {
        setIsShipToSameAsBillTo(isChecked);
        const shippingErrors = ["addressLineOne", "city", "state", "zipCode"];
        if (isChecked) {
            setAddressLineOne(billToAddressLineOne);
            setAddressLineTwo(billToAddressLineTwo);
            setCity(billToCity);
            setSelectedState(billToSelectedState);
            setZipCode(billToZipCode);
            shippingErrors.forEach(inputName => {
                delete formErrors[inputName as keyof FormErrorsStateType];
                setFormErrors(formErrors);
            });
        } else {
            setAddressLineOne("");
            setAddressLineTwo("");
            setCity("");
            setSelectedState(undefined);
            setZipCode("");
            const selectStateEl: HTMLSelectElement | null = document.querySelector('[name="state"]');
            if (selectStateEl) {
                validateHandler(selectStateEl);
            }
            shippingErrors.forEach(inputName => {
                if (!(inputName === "state" || inputName === "country")) {
                    setFormErrors(prevState => {
                        return {
                            ...prevState,
                            [inputName]: "",
                        };
                    });
                }
            });
        }
        setIsButtonDisabled(validateSubmitDisabled(formErrors));
    };

    const firstNameChangeHandler = (input: HTMLInputElement) => {
        validateHandler(input);
        setFirstName(input.value);
    };
    const jobTitleChangeHandler = (input: HTMLInputElement) => {
        validateHandler(input);
        setJobTitle(input.value);
    };
    const lastNameChangeHandler = (input: HTMLInputElement) => {
        validateHandler(input);
        setLastName(input.value);
    };
    const companyNameChangeHandler = (input: HTMLInputElement) => {
        validateHandler(input);
        setCompanyName(input.value);
    };
    const cityChangeHandler = (input: HTMLInputElement) => {
        validateHandler(input);
        setCity(input.value);
    };

    const addressLineOneChangeHandler = (input: HTMLInputElement) => {
        validateHandler(input);
        setAddressLineOne(input.value);
    };

    const addressLineTwoChangeHandler = (input: HTMLInputElement) => {
        setAddressLineTwo(input.value);
    };

    const emailChangeHandler = (input: HTMLInputElement) => {
        validateHandler(input);
        setEmail(input.value);
    };

    const phoneNumberChangeHandler = (input: HTMLInputElement) => {
        validateHandler(input);
        setPhoneNumber(formatPhoneNumber(input.value));
    };

    const faxChangeHandler = (input: HTMLInputElement) => {
        validateHandler(input);
        setFax(formatPhoneNumber(input.value));
    };

    const zipCodeChangeHandler = (input: HTMLInputElement) => {
        validateHandler(input);
        setZipCode(formatZipCode(input.value));
    };

    const billToAddressLineOneChangeHandler = (input: HTMLInputElement) => {
        validateHandler(input);
        setBillToAddressLineOne(input.value);
        if (IsShipToSameAsBilling) {
            setAddressLineOne(input.value);
        }
    };

    const billToAddressLineTwoChangeHandler = (input: HTMLInputElement) => {
        setBillToAddressLineTwo(input.value);
        if (IsShipToSameAsBilling) {
            setAddressLineTwo(input.value);
        }
    };

    const billToCityChangeHandler = (input: HTMLInputElement) => {
        validateHandler(input);
        setBillToCity(input.value);
        if (IsShipToSameAsBilling) {
            setCity(input.value);
        }
    };

    const handleBillToCountryChange: SelectProps["onChange"] = event => {
        const country = countries?.find(c => c.id === event.target.value);
        setBillToSelectedCountry(country);
        if (IsShipToSameAsBilling) {
            setSelectedCountry(country);
        }
    };

    const handleBillToStateChange: SelectProps["onChange"] = event => {
        const state = selectedCountry?.states?.find(s => s.id === event.target.value);
        validateHandler(event.target);
        setBillToSelectedState(state);
        if (IsShipToSameAsBilling) {
            setSelectedState(state);
        }
    };

    const billToZipCodeChangeHandler = (input: HTMLInputElement) => {
        validateHandler(input);
        setBillToZipCode(formatZipCode(input.value));
        if (IsShipToSameAsBilling) {
            setZipCode(formatZipCode(input.value));
        }
    };

    const businessTypeChangeHandler = (businessType: string) => {
        setBusinessType(businessType);
    };

    const passwordChangeHandler = (input: HTMLInputElement) => {
        validateConfirmPassword(input.value, confirmPassword);
        validatePassword({
            password: input.value,
            onComplete: errorMessage => {
                setPasswordErrorMessage(errorMessage);
            },
        });
        setPassword(input.value);
        pwdHandler(input);
    };

    const confirmPasswordChangeHandler = (input: HTMLInputElement) => {
        setConfirmPassword(input.value);
        validateConfirmPassword(password, input.value);
        pwdHandler(input);
    };

    const onSuccessHandler = () => {
        completeAction(3);
        setErrorMessage("");
        setIsButtonDisabled(true);
        toasterContext.addToast({
            body: siteMessage("Your account has been successfully created"),
            messageType: "success",
            timeoutLength: 2500,
        });
        function getDataLayer() {
            return (window as any).dataLayer;
        }
        const dataLayer = getDataLayer();
        if (dataLayer) {
            dataLayer.push({
                event: "sign_up",
                sign_up_type: "new",
            });
        }

        setTimeout(() => {
            try{
            signIn({
                userName: createAccountData.email,
                password: createAccountData.password,
                rememberMe: false,
                returnUrl: onePageCheckoutPageUrl,
                onError(error) {
                    
                }
                
            });
        } catch (error){
           
        }
        }, 500);
    };

    const submitHandler = async () => {
        setIsButtonDisabled(true);

        const isReCaptchaValid = await validateReCaptcha({ location: "CreateAccount", recaptchaRef });
        if (!isReCaptchaValid) {
            setIsButtonDisabled(false);
            setIsSubmitted(false);
            return;
        }

        // if (!isSubmitted) {
        //     setIsSubmitted(true);
        // }
        setIsSubmitted(true);
        const formData = JSON.parse(JSON.stringify(createAccountData));
        delete formData.tempoData;

        await addAccount({
            ...formData,
            onSuccess: onSuccessHandler,
            onError: error => {
           
                toasterContext.addToast({
                    body: siteMessage(error),
                    messageType: "warning",
                    timeoutLength: 2500,
                });
                setIsButtonDisabled(false);
                setErrorMessage(error);
                setIsSubmitting(false);
                setIsSubmitted(false);
            },
            onComplete(resultProps) {
            
                if (resultProps.apiResult?.successful) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.();
                } else if (resultProps.apiResult?.errorMessage) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc

                    this.onError?.(resultProps.apiResult.errorMessage);
                }
            },
        });
    };

    const pwdHandler = (input: HTMLInputElement) => {
        const pwd = input.name === "password" ? input.value : password;
        const confirmPwd = input.name === "confirmPassword" ? input.value : confirmPassword;

        const isValidate: ErrorObjectType | undefined =
            input.name === "password"
                ? validatePasswordHandler(pwd, confirmPwd)
                : validateConfirmPasswordHandler(pwd, confirmPwd);
        if (isValidate === undefined) {
         
            delete formErrors["password" as keyof FormErrorsStateType];
            delete formErrors["confirmPassword" as keyof FormErrorsStateType];
            setFormErrors(formErrors);
        } else {
          
            if(input.name == "password")
            setFormErrors({
                ...formErrors,
                password: isValidate ? isValidate.errorMsg : isValidate,
            });
            if(input.name == "confirmPassword")
            setFormErrors({
                ...formErrors,
                confirmPassword: isValidate ? isValidate.errorMsg : isValidate,
            });
        }
        setIsButtonDisabled(validateSubmitDisabled(formErrors));
    };

    const handleCountryChange: SelectProps["onChange"] = event => {
        const country = countries?.find(c => c.id === event.target.value);
        setSelectedCountry(country);
    };

    const handleStateChange: SelectProps["onChange"] = event => {
        const state = selectedCountry?.states?.find(s => s.id === event.target.value);
        validateHandler(event.target);
        setSelectedState(state);
    };

    const componentStyles: MessageFilterStyles = styles.messageFilter || {};

    const loginHandler = () => {
        completeAction(4);
    };

    const [openTabs, setOpenTabs] = useState<number[]>(tabState.openTabs);
    const [completedTabs, setCompletedTabs] = useState<number[]>(tabState.completedTabs);
    const [currentTab, setCurrentTab] = useState<number>(tabState.currentTab);

    useEffect(() => {
       }, [openTabs, completedTabs, currentTab]);
    const completeAction = (index: number) => {
        setCurrentTab(index + 1);
        if (!completedTabs.includes(index)) {
            setCompletedTabs([...completedTabs, index]);
            setOpenTabs(prev => [...prev, index + 1]);
        }
    };

    const isInContentAdmin = () => {
        try {
            const parentUrl = window?.parent?.location?.toString().toLowerCase();
            return parentUrl.includes("/contentadmin/");
        } catch (error) {
            // Catch cross-origin errors (if parent URL can't be accessed)
            return false;
        }
    };
    React.useEffect(() => {
        if (isInContentAdmin()) {
            completeAction(3);
        }
    }, []);
    React.useEffect(() => {
        SetOnePageTabData({
            openTabs,
            completedTabs,
            currentTab,
        });
    }, [openTabs, completedTabs, currentTab, SetOnePageTabData]);

    useEffect(() => {
        if (isAuthenticated) {
            completeAction(3);
        }
    }, [isAuthenticated]);

    const [formType, setFormType] = React.useState("N");

    const handleFormsType = (formType: string) => {
        setFormType(formType);
    };

    const [customerNumberAvailable, setCustomerNumberAvailable] = useState("");
    const [customerNumber, setCustomerNumber] = useState("");
    const [showCustomerNumber, setShowCustomerNumber] = useState(false);
    const [showCustomerNumberMessage, setShowCustomerNumberMessage] = useState(false);
    const handleCustomerNumberAvailable: SelectProps["onChange"] = event => {
        const customerNumberAvailableValue = event.target.value;
        setCustomerNumberAvailable(customerNumberAvailableValue);
        if (customerNumberAvailableValue === "Yes") {
            setShowCustomerNumber(true);
            setShowCustomerNumberMessage(false);
        } else if (customerNumberAvailableValue === "I need help from Customer Service to look it up") {
            setShowCustomerNumber(false);
            setShowCustomerNumberMessage(true);
        } else {
            setShowCustomerNumber(false);
            setShowCustomerNumberMessage(false);
        }
    };
    const customerNumberChangeHandler = (input: HTMLInputElement) => {
        setCustomerNumber(input.value);
    };
    const customerNumberBlurHandler = (input: HTMLInputElement) => {
        // validateHandler(input);
    };

    useEffect(() => {
        setOnePageCreateAccountData({ customerNumber, formType, password });
    }, [customerNumber, password, formType]);

    const isFormInvalid = () => {
        const {
            addressLineOne,
            city,
            companyName,
            email,
            formType: accountFormType,
            jobTitle,
            zipCode,
            phoneNumber,
            state,
            lastName,
            firstName,
            businessType,
        } = createAccountData;

        const hasAllRequiredFields =
            addressLineOne &&
            city &&
            companyName &&
            email &&
            accountFormType &&
            jobTitle &&
            zipCode &&
            phoneNumber &&
            state &&
            lastName &&
            firstName &&
            businessType;

        const isPasswordValid = password && confirmPassword && formType && password === confirmPassword;

        const isCustomerInfoValid =
        accountFormType === "Y" ? 
            (showCustomerNumber || showCustomerNumberMessage) && (!showCustomerNumber || customerNumber)
        : true;
            return !(hasAllRequiredFields && isPasswordValid && isCustomerInfoValid);
    };

    return (
        <>
            {isAuthenticated ? (
                <GridItem {...styles.companyDetailsGridItem}>
                    <StyledWrapper {...styles.userDetailsWrapper}>
                        <Typography {...styles.userLabel}>{translate("New Customer Account Created")}</Typography>
                        <Typography {...styles.userName}>{translate("Password Created")}</Typography>
                    </StyledWrapper>
                </GridItem>
            ) : (
                <StyledWrapper>
                    <LoadingOverlay loading={isSubmitted} spinnerProps={{ color: "primary" }}>
                        <GridContainer {...styles.mainGridContainer}>
                            <GridContainer>
                                <GridItem width={12}>
                                    <StyledWrapper {...styles.businessDone}>
                                        <Typography {...styles.inputHeader}>
                                            {translate("Have You Previously Done Business with SunSource? *")}
                                        </Typography>
                                        <CustomRadio>
                                            <input
                                                type="radio"
                                                id="yFormType"
                                                name="formType"
                                                value="Y"
                                                checked={formType === "Y"}
                                                onChange={e => {
                                                    handleFormsType(e.target.value);
                                                }}
                                                required
                                            />
                                            <label htmlFor="yFormType">
                                                {siteMessage(
                                                    "Yes, our company/organisation has an existing account with SunSource",
                                                )}
                                            </label>
                                        </CustomRadio>

                                        <CustomRadio>
                                            <input
                                                type="radio"
                                                id="nFormType"
                                                name="formType"
                                                value="N"
                                                checked={formType === "N"}
                                                onChange={e => {
                                                    handleFormsType(e.target.value);
                                                }}
                                                required
                                            />
                                            <label htmlFor="nFormType">
                                                {siteMessage("No, we are a new customer")}
                                            </label>
                                        </CustomRadio>
                                    </StyledWrapper>
                                </GridItem>
                            </GridContainer>
                        {formType == "Y" &&
                            <GridContainer>
                                <GridItem {...styles.fieldsGridItem}>
                                    <Select
                                        {...styles.customerNumberAvailableTextField}
                                        value={customerNumberAvailable}
                                        onChange={handleCustomerNumberAvailable}
                                        required
                                    >
                                        <option value="0">{translate("Do you have a Customer # Available? *")}</option>
                                        <option value="Yes">{translate("Yes")}</option>
                                        <option value="I need help from Customer Service to look it up">
                                            {translate("I need help from Customer Service to look it up")}
                                        </option>
                                    </Select>
                                </GridItem>
                                {showCustomerNumber && showCustomerNumber && (
                                    <GridItem {...styles.fieldsGridItem}>
                                        <TextField
                                            {...styles.customerNumberTextField}
                                            name="customerNumber"
                                            value={customerNumber}
                                            data-test-selector="createAccount_customerNumber"
                                            onChange={e => customerNumberChangeHandler(e.currentTarget)}
                                            onBlur={e => customerNumberBlurHandler(e.currentTarget)}
                                            error={!!formErrors.customerNumber && formErrors.customerNumber}
                                            iconProps={{
                                                color: "danger",
                                                src: formErrors.customerNumber ? AlertTriangle : undefined,
                                            }}
                                            placeholder="Enter Customer Number *"
                                            required
                                        />
                                    </GridItem>
                                )}
                                {showCustomerNumberMessage && (
                                    <Typography {...styles.needHelpCustomerService}>
                                        {translate(
                                            "Please continue creating your user profile and our customer service team will help align your account upon receiving your order.",
                                        )}
                                    </Typography>
                                )}
                            </GridContainer>
                        }
                            <GridContainer>
                                <GridItem width={12} {...styles.createPasswordTitleWrapper}>
                                    <Typography {...styles.inputHeader}>{translate("Create Password")}</Typography>
                                </GridItem>

                                <GridItem {...styles.fieldsGridItem}>
                                    <TextField
                                        id="password"
                                        {...styles.passwordTextField}
                                        placeholder="Password *"
                                        type={showPassword ? undefined : "password"}
                                        value={password}
                                        onChange={e => passwordChangeHandler(e.currentTarget)}
                                        iconProps={{ ...styles.passwordIcon, src: showPassword ? EyeOff : Eye }}
                                        iconClickableProps={{
                                            onClick: (e: any) => {
                                                e.preventDefault();
                                                setShowPassword(!showPassword);
                                            },
                                        }}
                                        data-test-selector="createAccount_password"
                                        onBlur={e => pwdHandler(e.currentTarget)}
                                        error={!!formErrors.password && formErrors.password}
                                        name="password"
                                        required
                                    />
                                </GridItem>
                                <GridItem {...styles.fieldsGridItem}>
                                    <TextField
                                        id="confirm-password"
                                        {...styles.confirmPasswordTextField}
                                        placeholder="Confirm Password *"
                                        type={showConfirmPassword ? undefined : "password"}
                                        value={confirmPassword}
                                        onChange={e => confirmPasswordChangeHandler(e.currentTarget)}
                                        iconProps={{
                                            ...styles.confirmPasswordIcon,
                                            src: showConfirmPassword ? EyeOff : Eye,
                                        }}
                                        iconClickableProps={{
                                            onClick: (e: any) => {
                                                e.preventDefault();
                                                setShowConfirmPassword(!showConfirmPassword);
                                            },
                                        }}
                                        data-test-selector="createAccount_confirmPassword"
                                        onBlur={e => pwdHandler(e.currentTarget)}
                                        name="confirmPassword"
                                        error={!!formErrors.confirmPassword && formErrors.confirmPassword}
                                        required
                                    />
                                </GridItem>
                            </GridContainer>
                            <GridContainer className="userPasswordDiv">
                                <GridItem {...styles.fieldsGridItem}>
                                    <StyledWrapper {...styles.passwordValidationMessage}>
                                        <ul>
                                            <li>{`Contains at least ${accountSettings.passwordMinimumLength} ${
                                                accountSettings.passwordMinimumLength > 1 ? `characters` : `character`
                                            }`}</li>
                                            <li>
                                                {accountSettings.passwordRequiresUppercase &&
                                                accountSettings?.passwordRequiresLowercase
                                                    ? translate("Includes lowercase and uppercase letters")
                                                    : accountSettings.passwordRequiresUppercase
                                                    ? "Includes uppercase letters"
                                                    : accountSettings?.passwordRequiresLowercase
                                                    ? "Includes lowercase letters"
                                                    : ""}
                                            </li>
                                            <li>
                                                {accountSettings?.passwordRequiresDigit
                                                    ? translate("Includes at least one number")
                                                    : ""}
                                            </li>

                                            <li>
                                                {accountSettings?.passwordRequiresSpecialCharacter
                                                    ? translate("Includes at least one symbol (e.g.,!?#$%&*)")
                                                    : ""}
                                            </li>
                                        </ul>
                                    </StyledWrapper>
                                </GridItem>
                                <GridItem {...styles.reCaptchaGridItem}>
                                    <ReCaptcha recaptchaRef={recaptchaRef} location="CreateAccount" />
                                </GridItem>
                                <GridItem {...styles.loginButtonSection} width={12}>
                                    <Button
                                        {...styles.loginBtn}
                                        key="addAll"
                                        disabled={isFormInvalid()}
                                        onClick={submitHandler}
                                        data-test-selector="cartActionsAddAllToList"
                                    >
                                        {translate("Confirm")}
                                    </Button>
                                </GridItem>
                                <Typography style={{paddingRight:"15px",width:"100%",textAlign:"right", color: "red" }}>{errorMessage}</Typography>
                            </GridContainer>
                        </GridContainer>
                    </LoadingOverlay>
                </StyledWrapper>
            )}
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(OnePageCustomerValidationAndSetPasswordForm);
